import { PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { floorsActions } from 'reduxStore/floors/slice';
import { UploadBox } from 'zComponents/atoms';
import { Button, Divider, Flex, Form, Input, InputNumber, Modal, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const AddFloorModal = ({ children, open, setOpen, siteID }) => {
	//Hooks
	const [form] = Form.useForm();
	const {
		token: {
			paddingXL,
			colorText,
			colorTextLabel,
			fontSizeSM,
			paddingXXL = 48,
			paddingMD,
			paddingXS,
			paddingSM,
			fontSizeLG,
			fontWeightStrong
		}
	} = theme.useToken();
	const uploadRef = useRef(null);
	const dispatch = useDispatch();

	// Redux State
	const { createFloorLoading: loading } = useSelector((state) => state.Floors);

	// Local State
	const [imagePath, setImagePath] = useState(null);
	const [image, setImage] = useState(null);

	//Determine who has permission to Add Floor
	const isPermittedCreateFloor = useManagePermittedRoles('addFloor');

	const handleCancel = () => {
		form.resetFields();
		setOpen(false);
		setImagePath(null);
		setImage(null);
	};

	const handleFinish = async (values) => {
		await form.validateFields();

		const payload = {
			siteID,
			name: values.name,
			dimensionX: values.xDimension ? Number(values.xDimension) : null,
			dimensionY: values.yDimension ? Number(values.yDimension) : null,
			url: imagePath
		};

		dispatch(floorsActions.createFloor({ data: removeEmptyOrNull(payload) }));
		handleCancel();
	};

	return (
		<Fragment>
			<Tooltip title={isPermittedCreateFloor ? 'Add Floor' : "You don't have permission to add a floor"}>
				{children ? (
					children
				) : (
					<Button
						type='primary'
						icon={<PlusOutlined />}
						onClick={() => setOpen(true)}
						disabled={!isPermittedCreateFloor}
					>
						Add Floor
					</Button>
				)}
			</Tooltip>

			<Modal
				open={open}
				onCancel={handleCancel}
				closeIcon
				centered
				destroyOnClose
				width={1434}
				footer={[
					<Flex gap={paddingSM} justify='flex-end'>
						<Button onClick={handleCancel} type='default'>
							Cancel
						</Button>

						<Button type='primary' htmlType='submit' form='floorForm' loading={loading}>
							Add Floor
						</Button>
					</Flex>
				]}
				styles={{ content: { padding: paddingXL }, footer: { marginTop: paddingXXL } }}
			>
				<Flex vertical style={{ marginBottom: paddingXL }}>
					<Title level={4} style={{ margin: 0, color: colorText, lineHeight: '28px' }}>
						Add New Floor
					</Title>
					<Text style={{ color: colorTextLabel, fontSize: fontSizeSM }}>Add a new floor to your site.</Text>
				</Flex>

				<Form
					id='floorForm'
					form={form}
					layout='vertical'
					onFinish={handleFinish}
					requiredMark={false}
					name='floorForm'
					// style={{ backgroundColor: 'purple' }}
				>
					<Flex gap={paddingXXL}>
						<Flex vertical gap={paddingMD} style={{ width: '100%' }}>
							<Form.Item
								name='name'
								label={<Text style={{ fontWeight: fontWeightStrong }}>Floor Name</Text>}
								style={{ marginBottom: 0 }}
								rules={[{ required: true, message: 'Please enter a floor name' }]}
							>
								<Input placeholder='eg. Level 1' />
							</Form.Item>
						</Flex>

						<Divider type='vertical' style={{ margin: `0px ${paddingXS}px`, height: 'auto' }} />

						<Flex gap={paddingXL} vertical style={{ width: '100%' }}>
							<Text style={{ fontSize: fontSizeLG, fontWeight: fontWeightStrong }}>
								Floor Plan (Optional)
							</Text>
							<Flex gap={paddingSM}>
								<Form.Item
									name='xDimension'
									label={
										<Text style={{ fontWeight: fontWeightStrong, color: colorTextLabel }}>
											X Dimension
										</Text>
									}
									style={{ width: '100%', marginBottom: 0 }}
									dependencies={['yDimension', 'url']}
									rules={[
										({ getFieldValue }) => ({
											validator(_, value) {
												const y = getFieldValue('yDimension');
												if ((y || imagePath) && !value) {
													return Promise.reject(new Error('X Dimension is required'));
												}
												return Promise.resolve();
											}
										})
									]}
								>
									<InputNumber placeholder='eg. 20' style={{ width: '100%' }} min={0} step={1} />
								</Form.Item>

								<Form.Item
									name='yDimension'
									label={
										<Text style={{ fontWeight: fontWeightStrong, color: colorTextLabel }}>
											Y Dimension
										</Text>
									}
									style={{ width: '100%', marginBottom: 0 }}
									dependencies={['xDimension', 'url']}
									rules={[
										({ getFieldValue }) => ({
											validator(_, value) {
												const x = getFieldValue('xDimension');
												if ((x || imagePath) && !value) {
													return Promise.reject(new Error('Y Dimension is required'));
												}
												return Promise.resolve();
											}
										})
									]}
								>
									<InputNumber placeholder='eg. 24.5' style={{ width: '100%' }} min={0} step={1} />
								</Form.Item>
							</Flex>

							<Form.Item
								name='url'
								label={<Text style={{ fontWeight: fontWeightStrong }}>Floor Plan</Text>}
								style={{ marginBottom: 0, padding: `0px 2px` }}
								dependencies={['xDimension', 'yDimension']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											const y = getFieldValue('yDimension');
											if ((x || y) && !imagePath) {
												return Promise.reject(new Error('Floor Layout is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<UploadBox
									entityID={3}
									photoUrl={imagePath || null}
									entityName='Floor'
									shape='square'
									cursor
									edit
									size={330}
									uploadRef={uploadRef}
									setImage={setImage}
									image={image}
									setImagePath={setImagePath}
									imagePath={imagePath}
									placeHolderUpload='Drag and drop or click to upload'
								/>
							</Form.Item>
						</Flex>
					</Flex>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default AddFloorModal;
